import Link from "@web-ui/adapters/link";
import Icon from "@web-ui/components/Icon";

/*
 * Button, restyled as the Tixora template's (HeroUI v3) button: seven variants, four sizes
 * (the kit's own 48px button is the default, Tixora's two sizes are sm and xs),
 * icon only, full width, and the hover, pressed, focus, disabled and pending states. Styles in
 * styles/controls.css. The kit's original flags (isBlack, isWhite, isGray, isStroke, isCircle)
 * still work and map onto the variants, so the kit's own components keep compiling unchanged.
 */
export type ButtonVariant = "primary" | "secondary" | "tertiary" | "outline" | "ghost" | "danger" | "danger-soft";

type Props = {
    className?: string;
    icon?: string;
    children?: React.ReactNode;
    variant?: ButtonVariant;
    /** md (default) is the Core 2 kit's 48px button; sm is Tixora's standard button, xs Tixora's small one, lg a large call to action. */
    size?: "xs" | "sm" | "md" | "lg";
    isIconOnly?: boolean;
    fullWidth?: boolean;
    /** Shows a spinner and blocks presses while an action runs. */
    isPending?: boolean;
    /** Kit flags, mapped: isBlack = primary, isWhite = tertiary, isGray = tertiary, isStroke = outline, isCircle = icon only. */
    isWhite?: boolean;
    isBlack?: boolean;
    isGray?: boolean;
    isCircle?: boolean;
    isStroke?: boolean;
};

type ButtonProps = Props &
    (
        | (React.ButtonHTMLAttributes<HTMLButtonElement> & { as?: "button" })
        | (React.AnchorHTMLAttributes<HTMLAnchorElement> & { as: "a" })
        | ({ as: "link"; href: string } & Omit<
              React.AnchorHTMLAttributes<HTMLAnchorElement>,
              "href"
          >)
    );

const resolveVariant = ({ variant, isBlack, isStroke, isWhite, isGray }: Props): ButtonVariant =>
    variant ?? (isBlack ? "primary" : isStroke ? "outline" : isWhite || isGray ? "tertiary" : "ghost");

const Button: React.FC<ButtonProps> = ({
    className,
    icon,
    children,
    variant,
    size = "md",
    isIconOnly,
    fullWidth,
    isPending,
    isWhite,
    isBlack,
    isGray,
    isCircle,
    isStroke,
    as = "button",
    ...props
}) => {
    const resolved = resolveVariant({ variant, isBlack, isStroke, isWhite, isGray });
    const classes = [
        "button",
        `button--${resolved}`,
        size !== "md" ? `button--${size}` : "",
        isIconOnly || isCircle ? "button--icon-only" : "",
        fullWidth ? "button--full-width" : "",
        className || "",
    ].filter(Boolean).join(" ");
    const content = (
        <>
            {isPending ? <span className="button__spinner" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
            {children}
        </>
    );

    if (as === "a") {
        return (
            <a className={classes} {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
                {content}
            </a>
        );
    }

    if (as === "link") {
        return (
            <Link href={(props as { href: string }).href} className={classes}>
                {content}
            </Link>
        );
    }

    const buttonProps = props as React.ButtonHTMLAttributes<HTMLButtonElement>;
    return (
        <button
            className={classes}
            data-pending={isPending || undefined}
            aria-busy={isPending || undefined}
            {...buttonProps}
            disabled={buttonProps.disabled}
        >
            {content}
        </button>
    );
};

export default Button;
